import { computed, ref, watch, type Ref } from 'vue';
import {
  aggregateRecipientFeeSummary,
  calcOptimalHourlyWage,
  calcRecipientFeeRowPayAmount,
  calcTotalSugaHoursFromSummary,
  floorHoursToHalfHour,
  getAnnualLeaveDays,
  type HomeWageClaimRow,
  type HomeWageRecipientFeeSummary,
} from '@/policies/homeWageSettingPolicy';

const HOURLY_STORAGE_PREFIX = 'caion_hw_hourly_';
const SEVERE_FEE_STORAGE_PREFIX = 'caion_hw_severe_fee_';
const CAREGIVER_HOURLY_STORAGE_PREFIX = 'caion_hw_caregiver_hourly_';
const SUGA_HOURS_STORAGE_PREFIX = 'caion_hw_suga_hours_';

function hourlyStorageKey(mCode: string, yearMonth: string, empKey: string) {
  return `${HOURLY_STORAGE_PREFIX}${mCode}_${yearMonth}_${empKey}`;
}

function severeFeeStorageKey(mCode: string, yearMonth: string, empKey: string) {
  return `${SEVERE_FEE_STORAGE_PREFIX}${mCode}_${yearMonth}_${empKey}`;
}

function caregiverHourlyStorageKey(mCode: string, yearMonth: string, empKey: string) {
  return `${CAREGIVER_HOURLY_STORAGE_PREFIX}${mCode}_${yearMonth}_${empKey}`;
}

function sugaHoursStorageKey(mCode: string, yearMonth: string, empKey: string) {
  return `${SUGA_HOURS_STORAGE_PREFIX}${mCode}_${yearMonth}_${empKey}`;
}

function applyRowPayAmount(
  row: HomeWageRecipientFeeSummary,
  hourlyWage: number
): HomeWageRecipientFeeSummary {
  if (row.isSevereRecipientSurcharge) return row;
  const pay = calcRecipientFeeRowPayAmount({
    serviceCount: row.serviceCount,
    sugaHoursPerVisit: row.sugaHoursPerVisit,
    hourlyWage,
    feeAmountPerVisit: row.feeAmountPerVisit,
  });
  return {
    ...row,
    hourlyWage,
    holidayPayAmount: 0,
    totalAmount: pay.totalAmount,
  };
}

/**
 * 수가시간 수동값 반영 후 합계금액 재계산
 * ★ 수가시간은 이제 자동 계산되므로, 이 함수는 시급만 반영하여 합계금액 재계산
 */
function applySugaHoursOverrides(
  rows: HomeWageRecipientFeeSummary[],
  _sugaHoursByGroup: Record<string, number>,
  hourlyWageByGroup: Record<string, number>
): HomeWageRecipientFeeSummary[] {
  return rows.map((row) => {
    if (row.isSevereRecipientSurcharge) return row;
    const hourlyWage = hourlyWageByGroup[row.groupKey] ?? row.hourlyWage ?? 0;
    return applyRowPayAmount(row, hourlyWage);
  });
}

function readStoredNumber(key: string): number {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeStoredNumber(key: string, value: number) {
  try {
    if (value > 0) sessionStorage.setItem(key, String(value));
    else sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function readNumberMap(key: string): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, number>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeNumberMap(key: string, map: Record<string, number>) {
  try {
    sessionStorage.setItem(key, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export interface HomeWageAutoFillInput {
  minHourlyWage: number;
  workDays: number;
  joinDate: string | null;
  asOfYear: number;
  asOfMonth: number;
}

/**
 * 청구내역 → 수급자·수가코드별 집계 (계산은 computeFromPolicy / 버튼 전용)
 */
export function useHomeWageClaimSummary(options: {
  claimRows: Ref<HomeWageClaimRow[]>;
  memberCode: Ref<string>;
  yearMonth: Ref<string>;
  empKey: Ref<string>;
  minHourlyWage?: Ref<number>;
}) {
  const hourlyWageByGroup = ref<Record<string, number>>({});
  const severeFeeByGroup = ref<Record<string, number>>({});
  const sugaHoursByGroup = ref<Record<string, number>>({});
  const caregiverHourlyWage = ref(0);
  const summaryRows = ref<HomeWageRecipientFeeSummary[]>([]);

  const clampCaregiverHourlyWage = (wage: number): number => {
    const min = Number(options.minHourlyWage?.value) || 0;
    const w = Number.isFinite(wage) && wage > 0 ? Math.floor(wage) : 0;
    if (min > 0) {
      if (w <= 0) return min;
      return Math.max(min, w);
    }
    return w;
  };

  const hourlyKey = computed(() => {
    const code = options.memberCode.value;
    const ym = options.yearMonth.value;
    const emp = options.empKey.value;
    if (!code || !ym || !emp) return '';
    return hourlyStorageKey(code, ym, emp);
  });

  const severeFeeKey = computed(() => {
    const code = options.memberCode.value;
    const ym = options.yearMonth.value;
    const emp = options.empKey.value;
    if (!code || !ym || !emp) return '';
    return severeFeeStorageKey(code, ym, emp);
  });

  const caregiverHourlyKey = computed(() => {
    const code = options.memberCode.value;
    const ym = options.yearMonth.value;
    const emp = options.empKey.value;
    if (!code || !ym || !emp) return '';
    return caregiverHourlyStorageKey(code, ym, emp);
  });

  const sugaHoursKey = computed(() => {
    const code = options.memberCode.value;
    const ym = options.yearMonth.value;
    const emp = options.empKey.value;
    if (!code || !ym || !emp) return '';
    return sugaHoursStorageKey(code, ym, emp);
  });

  const persistMaps = () => {
    if (hourlyKey.value) writeNumberMap(hourlyKey.value, hourlyWageByGroup.value);
    if (severeFeeKey.value) writeNumberMap(severeFeeKey.value, severeFeeByGroup.value);
    if (sugaHoursKey.value) writeNumberMap(sugaHoursKey.value, sugaHoursByGroup.value);
  };

  const loadStoredMaps = () => {
    const currentGroupKeys = summaryRows.value.map((r) => r.groupKey);
    // 청구 집계 전 sessionStorage 로드 시 maps를 비우지 않음 (groupKey 마이그레이션 불가)
    if (currentGroupKeys.length === 0) {
      const stored = caregiverHourlyKey.value ? readStoredNumber(caregiverHourlyKey.value) : 0;
      if (stored > 0) {
        setCaregiverHourlyWage(stored);
      }
      return;
    }
    const rawHourly = hourlyKey.value ? readNumberMap(hourlyKey.value) : {};
    const rawSevere = severeFeeKey.value ? readNumberMap(severeFeeKey.value) : {};
    hourlyWageByGroup.value = migrateOldGroupKeys(rawHourly, currentGroupKeys);
    severeFeeByGroup.value = migrateOldGroupKeys(rawSevere, currentGroupKeys);
    sugaHoursByGroup.value = sugaHoursKey.value ? readNumberMap(sugaHoursKey.value) : {};
    const stored = caregiverHourlyKey.value ? readStoredNumber(caregiverHourlyKey.value) : 0;
    // sessionStorage 비어 있을 때 0으로 덮어쓰지 않음 (저장 급여대장 로드 후 race 방지)
    if (stored > 0) {
      // ★ 내부 함수 사용 — 아래 rebuildSummaryRows()에서 일괄 처리
      setCaregiverHourlyWageInternal(stored);
    }
    rebuildSummaryRows();
  };

  const rebuildSummaryRows = () => {
    const rows = aggregateRecipientFeeSummary(
      options.claimRows.value,
      hourlyWageByGroup.value,
      severeFeeByGroup.value
    );
    summaryRows.value = applySugaHoursOverrides(
      rows,
      sugaHoursByGroup.value,
      hourlyWageByGroup.value
    );
  };

  /** 청구 로드 직후: 구조만(합계 0), 지급계산시급은 최저시급 이상 유지 */
  const rebuildStructureOnly = () => {
    hourlyWageByGroup.value = {};
    severeFeeByGroup.value = {};
    sugaHoursByGroup.value = {};
    // 초기 로드 시 caregiverHourlyWage가 아직 0이므로 0 전달
    summaryRows.value = aggregateRecipientFeeSummary(options.claimRows.value, {}, {});
    // ★ 내부 함수 사용 — 불필요한 rebuildSummaryRows 재호출 방지
    setCaregiverHourlyWageInternal(0);
  };

  /**
   * 자동채움: 중증수가·지급계산시급만 산출.
   * 수급자별 시급은 최저시급으로 채우지 않는다.
   */
  const computeFromPolicy = (input: HomeWageAutoFillInput) => {
    const minHourly = Math.max(0, Math.floor(input.minHourlyWage));
    sugaHoursByGroup.value = {};

    const baseRows = aggregateRecipientFeeSummary(options.claimRows.value, {}, {});

    const hourlyMap: Record<string, number> = {};
    const severeMap: Record<string, number> = {};

    for (const row of baseRows) {
      if (row.isSevereRecipientSurcharge && row.feeAmountPerVisit > 0) {
        severeMap[row.groupKey] = row.feeAmountPerVisit;
      }
    }

    hourlyWageByGroup.value = hourlyMap;
    severeFeeByGroup.value = severeMap;

    summaryRows.value = aggregateRecipientFeeSummary(
      options.claimRows.value,
      hourlyMap,
      severeMap
    );

    const targetMonthly = summaryRows.value.reduce((sum, r) => sum + r.totalAmount, 0);
    const totalSugaHours = calcTotalSugaHoursFromSummary(summaryRows.value);
    const annualLeaveDays = getAnnualLeaveDays(
      input.joinDate,
      input.asOfYear,
      input.asOfMonth
    );

    let caregiverHourly = minHourly;
    if (targetMonthly > 0 && totalSugaHours > 0 && input.workDays > 0) {
      const optimal = calcOptimalHourlyWage(
        targetMonthly,
        totalSugaHours,
        input.workDays,
        annualLeaveDays
      );
      if (optimal != null && optimal > minHourly) {
        caregiverHourly = optimal;
      }
    }

    const clamped = clampCaregiverHourlyWage(caregiverHourly);
    caregiverHourlyWage.value = clamped;
    if (caregiverHourlyKey.value && clamped > 0) {
      writeStoredNumber(caregiverHourlyKey.value, clamped);
    }
    persistMaps();

    return {
      targetMonthly,
      totalSugaHours,
      annualLeaveDays,
      caregiverHourlyWage: clamped,
    };
  };

  const grandTotal = computed(() =>
    summaryRows.value.reduce((sum, r) => sum + r.totalAmount, 0)
  );

  const setHourlyWage = (groupKey: string, wage: number) => {
    hourlyWageByGroup.value = { ...hourlyWageByGroup.value, [groupKey]: wage };
    persistMaps();
  };

  const setSevereFee = (groupKey: string, fee: number) => {
    severeFeeByGroup.value = { ...severeFeeByGroup.value, [groupKey]: fee };
    persistMaps();
  };

  const setSugaHours = (groupKey: string, hours: number) => {
    sugaHoursByGroup.value = { ...sugaHoursByGroup.value, [groupKey]: hours };
    persistMaps();
  };

  /**
   * 지급계산시급 설정 (내부용 — rebuildSummaryRows 미호출)
   * ★ 외부에서는 setCaregiverHourlyWageAndRebuild() 사용 권장
   */
  const setCaregiverHourlyWageInternal = (wage: number) => {
    const v = clampCaregiverHourlyWage(wage);
    caregiverHourlyWage.value = v;
    if (caregiverHourlyKey.value && v > 0) {
      writeStoredNumber(caregiverHourlyKey.value, v);
    }
  };

  /**
   * 지급계산시급 설정
   */
  const setCaregiverHourlyWage = (wage: number) => {
    setCaregiverHourlyWageInternal(wage);
  };

  /** 지급계산시급: 0·최저 미만 불가 → 당해 최저시급(또는 기존값 중 큰 값) */
  const ensureCaregiverHourlyAtLeastMin = () => {
    setCaregiverHourlyWage(caregiverHourlyWage.value);
    return caregiverHourlyWage.value;
  };

  const updateRowHourly = (groupKey: string, wage: number) => {
    setHourlyWage(groupKey, wage);
    summaryRows.value = summaryRows.value.map((row) => {
      if (row.groupKey !== groupKey || row.isSevereRecipientSurcharge) return row;
      return applyRowPayAmount(row, wage);
    });
  };

  const updateRowSugaHours = (groupKey: string, hours: number) => {
    const sugaHoursPerVisit = floorHoursToHalfHour(hours);
    setSugaHours(groupKey, sugaHoursPerVisit);
    summaryRows.value = summaryRows.value.map((row) => {
      if (row.groupKey !== groupKey || row.isSevereRecipientSurcharge) return row;
      const hourlyWage = hourlyWageByGroup.value[groupKey] ?? row.hourlyWage ?? 0;
      return applyRowPayAmount({ ...row, sugaHoursPerVisit }, hourlyWage);
    });
  };

  const updateRowSevereFee = (groupKey: string, feePerVisit: number) => {
    setSevereFee(groupKey, feePerVisit);
    summaryRows.value = summaryRows.value.map((row) => {
      if (row.groupKey !== groupKey || !row.isSevereRecipientSurcharge) return row;
      return {
        ...row,
        feeAmountPerVisit: feePerVisit,
        totalAmount: Math.round(row.serviceCount * feePerVisit),
      };
    });
  };

  const resetStoredMaps = () => {
    hourlyWageByGroup.value = {};
    severeFeeByGroup.value = {};
    sugaHoursByGroup.value = {};
    caregiverHourlyWage.value = 0;
    summaryRows.value = [];
    if (hourlyKey.value) sessionStorage.removeItem(hourlyKey.value);
    if (severeFeeKey.value) sessionStorage.removeItem(severeFeeKey.value);
    if (sugaHoursKey.value) sessionStorage.removeItem(sugaHoursKey.value);
    if (caregiverHourlyKey.value) sessionStorage.removeItem(caregiverHourlyKey.value);
  };

  /**
   * 기존 3파트 groupKey(수급자|인정번호|수가코드)를 새 4파트(+수가금액)로 마이그레이션
   * 현재 summaryRows의 groupKey 목록과 매칭하여 변환
   */
  const migrateOldGroupKeys = (
    oldMap: Record<string, number>,
    currentGroupKeys: string[]
  ): Record<string, number> => {
    const result: Record<string, number> = {};
    for (const [oldKey, value] of Object.entries(oldMap)) {
      if (!value || value <= 0) continue;

      const parts = oldKey.split('|');
      const prefix = parts.length >= 3 ? parts.slice(0, 3).join('|') : oldKey;

      // 1) groupKey 완전 일치
      if (currentGroupKeys.includes(oldKey)) {
        result[oldKey] = value;
        continue;
      }

      // 2) 수급자|인정번호|수가코드(3파트) 접두어로 당월 4파트 키에 매핑
      for (const newKey of currentGroupKeys) {
        const newPrefix = newKey.split('|').slice(0, 3).join('|');
        if (newPrefix === prefix && !result[newKey]) {
          result[newKey] = value;
        }
      }
    }
    return result;
  };

  /** DB에서 로드한 JSON 데이터로 maps 초기화 */
  const loadMapsFromDb = (data: {
    hourlyByGroup?: Record<string, number> | null;
    severeFeeByGroup?: Record<string, number> | null;
    sugaHoursByGroup?: Record<string, number> | null;
    payCalcHourly?: number;
  }) => {
    // 현재 청구내역 기반 groupKey 목록
    const currentGroupKeys = summaryRows.value.map((r) => r.groupKey);

    if (data.hourlyByGroup && typeof data.hourlyByGroup === 'object') {
      const migrated = migrateOldGroupKeys(data.hourlyByGroup, currentGroupKeys);
      // 빈 객체로 기존 maps를 덮어쓰지 않음 (lock 해제·DB 병합 시 중증·시급 소실 방지)
      if (Object.keys(migrated).length > 0) {
        hourlyWageByGroup.value = migrated;
      }
    }
    if (data.severeFeeByGroup && typeof data.severeFeeByGroup === 'object') {
      const migrated = migrateOldGroupKeys(data.severeFeeByGroup, currentGroupKeys);
      if (Object.keys(migrated).length > 0) {
        severeFeeByGroup.value = migrated;
      }
    }
    // ★ 수가시간은 이제 자동 계산되므로 DB에서 로드하지 않음 (무시)
    // if (data.sugaHoursByGroup && typeof data.sugaHoursByGroup === 'object') {
    //   sugaHoursByGroup.value = data.sugaHoursByGroup;
    // }
    if (data.payCalcHourly != null && Number(data.payCalcHourly) > 0) {
      // ★ 내부 함수 사용 — 아래 rebuildSummaryRows()에서 일괄 처리
      setCaregiverHourlyWageInternal(Number(data.payCalcHourly));
    }
    persistMaps();
    rebuildSummaryRows();
  };

  /** 현재 maps 상태를 저장용 객체로 반환 */
  const getMapsForSave = () => ({
    hourlyByGroup: Object.keys(hourlyWageByGroup.value).length > 0 ? hourlyWageByGroup.value : null,
    severeFeeByGroup: Object.keys(severeFeeByGroup.value).length > 0 ? severeFeeByGroup.value : null,
    // ★ 수가시간은 자동 계산되므로 더 이상 저장하지 않음
    sugaHoursByGroup: null,
  });

  watch([hourlyKey, severeFeeKey, sugaHoursKey, caregiverHourlyKey], loadStoredMaps);

  return {
    summaryRows,
    grandTotal,
    caregiverHourlyWage,
    hourlyWageByGroup,
    severeFeeByGroup,
    sugaHoursByGroup,
    setHourlyWage,
    setSevereFee,
    setSugaHours,
    setCaregiverHourlyWage,
  updateRowHourly,
  updateRowSugaHours,
  updateRowSevereFee,
  rebuildStructureOnly,
  rebuildSummaryRows,
  computeFromPolicy,
  ensureCaregiverHourlyAtLeastMin,
  resetStoredMaps,
  loadStoredMaps,
  loadMapsFromDb,
  getMapsForSave,
  migrateOldGroupKeys,
};
}
